import React from 'react';
import { Button } from '../ui/button';
import { useNavigate } from 'react-router-dom';
import { Mic, PenTool, BookOpen, Crown, ArrowRight, ArrowLeft, CheckCircle, Star } from 'lucide-react';
import CorporateHeader from '../CorporateHeader';
import Footer from '../Footer';
import Breadcrumb from '../Breadcrumb';

const PublishingGhostwritingPage = () => {
  const navigate = useNavigate();

  const packages = [
    {
      id: 'starter',
      name: 'Starter Package',
      tagline: 'For emerging authors',
      price: '$5,000',
      priceNote: 'Starting at',
      features: [
        'Up to 30,000 words',
        'Basic editing included',
        '2 revision rounds',
        '60-day delivery',
        'Digital manuscript delivery'
      ],
      cta: 'Apply Now',
    },
    {
      id: 'pro',
      name: 'Pro Package',
      tagline: 'For serious authors',
      price: '$15,000',
      priceNote: 'Starting at',
      popular: true,
      features: [
        'Up to 60,000 words',
        'Full developmental editing',
        '4 revision rounds',
        '90-day delivery',
        'Publishing strategy consultation',
        'Cover design coordination',
        'ISBN registration assistance'
      ],
      cta: 'Apply Now',
    },
    {
      id: 'premium',
      name: 'Premium Package',
      tagline: 'For legacy builders',
      price: '$35,000+',
      priceNote: 'Custom quote',
      features: [
        'Unlimited word count',
        'Full creative direction',
        'Unlimited revisions',
        'Expedited delivery options',
        'Full publishing support',
        'Marketing strategy included',
        'Launch support',
        'Ongoing consultation'
      ],
      cta: 'Schedule Consultation',
    },
  ];

  const services = [
    'Book Ghostwriting',
    'Memoir Development',
    'Business Books',
    'Self-Help & Motivation',
    'Fiction & Creative Writing',
    'Article & Blog Writing',
    'Speech Writing',
    'Content Strategy'
  ];

  return (
    <div className="min-h-screen bg-black">
      <CorporateHeader />
      
      {/* Hero */}
      <section className="pt-28 pb-20 bg-gradient-to-b from-blue-900/30 via-black to-black relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-600/20 via-transparent to-transparent"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Breadcrumb Navigation */}
          <Breadcrumb items={[
            { label: 'Divisions', path: null },
            { label: 'Publishing & Ghostwriting' }
          ]} />
          
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-xl flex items-center justify-center">
              <Mic className="w-8 h-8 text-white" />
            </div>
            <div>
              <p className="text-blue-400 text-sm font-semibold uppercase tracking-wider">Division</p>
              <h1 className="text-4xl lg:text-5xl font-black text-white">Publishing & Ghostwriting</h1>
            </div>
          </div>
          
          <p className="text-xl text-gray-400 max-w-3xl leading-relaxed mb-6">
            Manuscript development and authority positioning. Professional ghostwriting services with 18+ years of experience and proven celebrity client results.
          </p>
          
          <p className="text-2xl text-blue-400 font-semibold italic">
            "YOU think it, MY pen writes it, YOU own it"
          </p>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto mb-16">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Services We Offer</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {services.map((service) => (
                <div key={service} className="bg-gray-900/50 border border-gray-800 rounded-lg p-4 text-center hover:border-blue-600/50 transition-colors">
                  <span className="text-gray-300">{service}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="py-16 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Ghostwriting Packages</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Choose the package that fits your project scope. All packages include complete confidentiality and professional quality.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {packages.map((pkg) => (
              <div 
                key={pkg.id}
                className={`bg-gray-900/50 border rounded-2xl overflow-hidden transition-all duration-300 ${
                  pkg.popular 
                    ? 'border-blue-600 ring-1 ring-blue-600/30 scale-105' 
                    : 'border-gray-800 hover:border-blue-600/50'
                }`}
              >
                {pkg.popular && (
                  <div className="bg-blue-600 text-white text-center py-2 text-sm font-bold">
                    MOST POPULAR
                  </div>
                )}
                
                <div className="p-8">
                  <h3 className="text-xl font-bold text-white mb-1">{pkg.name}</h3>
                  <p className="text-gray-500 text-sm mb-6">{pkg.tagline}</p>
                  
                  <div className="mb-6">
                    <span className="text-sm text-gray-500">{pkg.priceNote}</span>
                    <div className="text-3xl font-bold text-blue-400">{pkg.price}</div>
                  </div>
                  
                  <ul className="space-y-3 mb-8">
                    {pkg.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-gray-300 text-sm">
                        <CheckCircle className="w-5 h-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <Button 
                    onClick={() => navigate('/start-project')}
                    className={`w-full font-semibold ${
                      pkg.popular 
                        ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                        : 'bg-gray-800 hover:bg-gray-700 text-white'
                    }`}
                  >
                    {pkg.cta}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Why Choose Our Ghostwriting?</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { icon: Star, title: '18+ Years Experience', desc: 'Proven track record in publishing excellence' },
                { icon: Crown, title: 'Celebrity Client Portfolio', desc: 'Trusted by high-profile clients and bestselling authors' },
                { icon: PenTool, title: 'Complete Confidentiality', desc: 'Your story, your name, your ownership' },
                { icon: BookOpen, title: 'Full Publishing Support', desc: 'From manuscript to market, we guide you through' },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.title} className="flex items-start gap-4 p-6 bg-gray-900/50 border border-gray-800 rounded-xl">
                    <div className="w-12 h-12 bg-blue-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-bold text-white mb-1">{item.title}</h3>
                      <p className="text-gray-400 text-sm">{item.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-b from-black to-blue-900/20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Tell Your Story?</h2>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto">
            Submit your project application and let's discuss how we can bring your vision to life.
          </p>
          <Button 
            onClick={() => navigate('/start-project')}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-4 text-lg"
          >
            Start Your Project
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default PublishingGhostwritingPage;
